import { useEffect, useRef, useState } from "react";
import { Chapter, Reveal } from "../components/Reveal";
import { LogoMark } from "../components/Logo";
import { CAL_LINK, BOOK_IMG } from "../lib/site";
import { useLang } from "../lib/i18n";

export const BookCall = () => {
  const { t, lang } = useLang();
  const [calReady, setCalReady] = useState(false);
  const readyRef = useRef(false);

  useEffect(() => {
    const el = document.querySelector("#cal-inline-embed");
    if (!el || el.dataset.calDone) return;
    el.dataset.calDone = "1";

    window.__calLinkReady = () => {
      readyRef.current = true;
      setCalReady(true);
    };

    const snippet = document.createElement("script");
    snippet.type = "text/javascript";
    snippet.textContent = `
      (function (C, A, L) { let p = function (a, ar) { a.q.push(ar); }; let d = C.document; C.Cal = C.Cal || function () { let cal = C.Cal; let ar = arguments; if (!cal.loaded) { cal.ns = {}; cal.q = cal.q || []; d.head.appendChild(d.createElement("script")).src = A; cal.loaded = true; } if (ar[0] === L) { const api = function () { p(api, arguments); }; const namespace = ar[1]; api.q = api.q || []; if (typeof namespace === "string") { cal.ns[namespace] = cal.ns[namespace] || api; } p(cal, ar); return; } p(cal, ar); }; })(window, "https://app.cal.com/embed/embed.js", "init");
      Cal("init", { origin: "https://cal.com" });
      Cal("inline", {
        elementOrSelector: "#cal-inline-embed",
        calLink: "${CAL_LINK}",
        config: { theme: "light", useSlotsViewOnSmallScreen: "true", lang: "${lang}" }
      });
      Cal("ui", { language: "${lang}", cssVarsPerTheme: { light: { spacing: "0.2rem" }, dark: { spacing: "0.2rem" } } });
      Cal("on", { action: "linkReady", callback: function () { window.__calLinkReady && window.__calLinkReady(); } });
    `;
    document.body.appendChild(snippet);

    const watchdog = setTimeout(() => {
      const frame = document.querySelector("#cal-inline-embed iframe");
      if (frame && !readyRef.current) {
        const src = frame.src;
        frame.src = "about:blank";
        requestAnimationFrame(() => { frame.src = src; });
      }
    }, 20000);

    return () => clearTimeout(watchdog);
  }, []);

  return (
    <section id="gesprek" data-testid="book-a-call-section" className="relative border-t border-edge/40 py-24 md:py-36">
      <div className="mx-auto max-w-5xl px-6 md:px-12">
        <Chapter number="04" label={t.chBook} testid="book-call-chapter" />
        <Reveal delay={0.08}>
          <h2 className="mt-6 font-archivo text-3xl font-black tracking-tight text-cream md:text-5xl" data-testid="book-call-heading">
            {t.bookHeading}
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-tan sm:text-base" data-testid="book-call-copy">
            {t.bookCopy}
          </p>
        </Reveal>
        <div>
          <div className="mt-10 overflow-hidden rounded-[12px] border border-edge/70 bg-white shadow-xl shadow-black/10" data-testid="cal-embed-frame">
            <div className="relative h-40 overflow-hidden md:h-48" data-testid="book-call-image-frame">
              <img src={BOOK_IMG} alt="Persoonlijk gesprek met 4thscenery" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/40 to-amber/20" />
              <div className="absolute bottom-4 left-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint">4thscenery · intake</p>
                <p className="mt-1 font-archivo text-lg font-extrabold text-cream">
                  {lang === "nl" ? "15 min · 1-op-1 · vrijblijvend" : "15 min · 1-on-1 · no strings attached"}
                </p>
              </div>
            </div>
            <div className="relative p-2 md:p-3">
              {!calReady && (
                <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white" aria-hidden="true">
                  <LogoMark size={16} />
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint">{t.calLoading}</p>
                </div>
              )}
              <div id="cal-inline-embed" data-testid="cal-inline-embed" className="relative bg-white" style={{ minHeight: 640, width: "100%" }} />
            </div>
          </div>
          <p className="mt-4 text-center">
            <a
              href={`https://cal.com/${CAL_LINK}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-xs uppercase tracking-[0.2em] text-faint underline-offset-4 transition-colors duration-300 hover:text-amberdeep hover:underline"
              data-testid="cal-direct-link"
            >
              {t.calDirect}
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};
